import React, { Fragment, useEffect, useMemo, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { useTheme } from "@emotion/react";
import { Box, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { Add, Close, Description, FiberManualRecord } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import CommonDialog from "../../common/components/CommonDialog";
import Dropzone from "react-dropzone";
import DynamicCalendar from "./DynamicCalendar";
import Activity from "./Activity";
import Notification from "./Notification";
import CreateEventDialog from "./CreateEventDialog";
import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getAllCalendarEvent, getCalendarEvent, uploadEvent } from "../../api/calendarApi";
import { setEventList } from "../../features/calenderSlice";
import { UserTypeId } from "../../common/const";
import moment from "moment";

const getWeekDates = (date) => {
    const currentDate = new Date(date);
    const day = currentDate.getDay();
    const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1); // Monday start

    const weekDates = [];
    for (let i = 0; i < 7; i++) {
        const weekDate = new Date(currentDate);
        weekDate.setDate(diff + i);
        weekDates.push({
            date: new Date(weekDate),
            day: weekDate.getDate(),
            month: weekDate.getMonth(),
            year: weekDate.getFullYear(),
            dayName: weekDate.toLocaleDateString('en-US', { weekday: 'long' }),
            fullDayName: weekDate.toLocaleDateString('en-US', { weekday: 'long' }),
            isToday: weekDate.toDateString() === new Date().toDateString()
        });
    }
    return weekDates;
};

const Calendar = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const [eventOpen, setEventOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [fileNames, setFileNames] = useState([]);
    const [events, setEvents] = useState([]);
    const today = new Date();
    const [currentDate, setCurrentDate] = useState(today);
    const [viewType, setViewType] = useState('month');
    const [selectedDate, setSelectedDate] = useState(today);

    const weekDates = useMemo(() => getWeekDates(currentDate), [currentDate]);

    const getEvents = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getCalendarEvent(user?.user_id, '');
            dispatch(setEventList({
                list: response.data
            }))
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const getAllEvents = async () => {
        try {
            dispatch(setLoading({ loading: true }));
            let response;

            if (viewType === 'month') {
                response = await getAllCalendarEvent(
                    user?.user_id,
                    currentDate.getMonth() + 1,
                    currentDate.getFullYear(),
                    null,
                    null,
                    user?.student_id
                );
            } else if (viewType === 'day') {
                // Get start and end of the day in UTC
                const startOfDay = moment(currentDate).utc().startOf('day').format();
                const endOfDay = moment(currentDate).utc().endOf('day').format();

                response = await getAllCalendarEvent(
                    user?.user_id,
                    null,
                    null,
                    startOfDay,
                    endOfDay,
                    user?.student_id
                );
            } else if (viewType === 'week') {
                // Get start of first day and end of last day in UTC
                const startOfWeek = moment(weekDates[0].date).utc().startOf('day').format();
                const endOfWeek = moment(weekDates[6].date).utc().endOf('day').format();

                response = await getAllCalendarEvent(
                    user?.user_id,
                    null,
                    null,
                    startOfWeek,
                    endOfWeek,
                    user?.student_id
                );
            }

            setEvents(response.data);
            dispatch(setLoading({ loading: false }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message ||
                err.response?.data?.message ||
                err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({ loading: false }));
        }
    };
    useEffect(() => {
        if (user?.user_id) {
            getEvents();
        }
    }, [user?.user_id]);

    useEffect(() => {
        if (user?.user_id && currentDate) {
            getAllEvents();
        }
    }, [user?.user_id, currentDate, viewType])

    const handleDrop = acceptedFiles => {
        setFileNames(acceptedFiles?.map(files => files));
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            {eventOpen && (
                <CreateEventDialog
                    eventOpen={eventOpen}
                    setEventOpen={setEventOpen}
                    getEvents={getEvents}
                    getAllEvents={getAllEvents}
                />
            )}
            <CommonDialog
                open={scheduleOpen}
                size="xs"
                title={
                    <Fragment>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box>
                                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Upload My Schedule</Typography>
                                <Typography fontSize={13} color={theme.palette.background.dark}>Create a new calendar event or task</Typography>
                            </Box>
                            <Close
                                sx={{
                                    color: theme.palette.background.dark, fontSize: 20,
                                    float: 'right',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    setFileNames([]);
                                    setScheduleOpen(false);
                                }}
                            />
                        </Box>
                        <Divider />
                    </Fragment>
                }
                content={
                    <Grid container mt={2} mb={2}>
                        <Box sx={{ width: '100%' }}>
                            {fileNames.length > 0 ? (
                                // Show selected files
                                <Box sx={{ mt: 2 }}>
                                    {fileNames.map((file, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                                border: '1px solid',
                                                borderColor: 'grey.300',
                                                borderRadius: 1,
                                                mb: 1,
                                                backgroundColor: 'background.paper'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Description color="primary" />
                                                <Box>
                                                    <Typography variant="body1">{file.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(file.size / 1024).toFixed(2)} KB
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => setFileNames(fileNames.splice(index, -1))}
                                                color="error"
                                            >
                                                <Close />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                // Show dropzone when no files
                                <Dropzone
                                    onDrop={handleDrop}
                                    accept='.csv'
                                >
                                    {({ getRootProps, getInputProps, isDragActive }) => (
                                        <Box
                                            {...getRootProps()}
                                            sx={{
                                                border: '2px dashed',
                                                borderColor: '#D8DAEB',
                                                borderRadius: 2,
                                                padding: { xs: 3, md: 6 },
                                                textAlign: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                minHeight: 100,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <input {...getInputProps()} />
                                            <img src="/Images/sound.png" alt="upload" />
                                            <Typography fontWeight={600} gutterBottom>
                                                Drag and drop your files
                                            </Typography>
                                            <Typography fontSize={14} color={theme.palette.background.dark} gutterBottom>
                                                CSV
                                            </Typography>
                                            <CommonButton
                                                label="Select File"
                                                color="Black"
                                                background="White"
                                                border={`1px solid ${theme.palette.background.secondary}`}
                                                width="100px"
                                                height="40px"
                                                marginBottom="0px"
                                                marginTop="10px"
                                            />
                                        </Box>
                                    )}
                                </Dropzone>
                            )}
                        </Box>
                    </Grid>
                }
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Download Sample"
                            size="small"
                            background={theme.palette.primary.main}
                            color={theme.palette.secondary.main}
                            fontWeight={700}
                            marginBottom="10px"
                            handleClick={async () => {
                                const link = document.createElement('a');
                                link.href = '/data/sample.csv'; // Path relative to public folder
                                link.download = 'Upload Schedule Sample.csv'; // Default filename for download
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                            }}
                        />
                        {user?.user_type_id === UserTypeId.Student && (
                            <CommonButton
                                label="Upload"
                                size="small"
                                background={theme.palette.secondary.main}
                                color={theme.palette.background.main}
                                fontWeight={700}
                                marginBottom="10px"
                                handleClick={async () => {
                                    try {
                                        dispatch(setLoading({
                                            loading: true
                                        }));
                                        const formData = new FormData();
                                        formData.append('userId', user?.user_id);
                                        formData.append('file', fileNames[0]);
                                        formData.append('studentId', user?.student_id);
                                        const response = await uploadEvent(formData);
                                        if (response.success) {
                                            showNotification(response.message, 'success');
                                            getAllEvents();
                                            getEvents();
                                            setFileNames([]);
                                            setLoading({
                                                loading: false
                                            });
                                            setScheduleOpen(false);
                                        }
                                    } catch (err) {
                                        const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                        showNotification(errorMessage, 'error');
                                        dispatch(setLoading({
                                            loading: false
                                        }))
                                    }
                                }}
                            />)}
                    </Fragment>
                )}
            />
            <Box
                sx={{
                    height: '85vh',
                    overflow: 'auto'
                }}
            >
                <PageHeader
                    title="My DCP Calendar"
                    content={
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                Home
                            </Typography>
                            <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                            <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                                My DCP Calendar
                            </Typography>
                        </Stack>
                    }
                    buttonContent={
                        user?.user_type_id === UserTypeId.Student && (
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <CommonButton
                                    width="130px"
                                    label="Add Event"
                                    fontWeight={600}
                                    startIcon={<Add />}
                                    marginBottom="0px"
                                    handleClick={() => setEventOpen(true)}
                                />
                                <CommonButton
                                    width="160px"
                                    label="Upload My Schedule"
                                    color={theme.palette.primary.main}
                                    background={theme.palette.secondary.main}
                                    fontWeight={500}
                                    marginBottom="0px"
                                    handleClick={() => setScheduleOpen(true)}
                                />
                            </Box>)
                    }
                />
                <Grid container spacing={2}>
                    <Grid item md={8.5}>
                        <DynamicCalendar
                            currentDate={currentDate}
                            setCurrentDate={setCurrentDate}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            viewType={viewType}
                            setViewType={setViewType}
                            events={events}
                            weekDates={weekDates}
                            getEvents={getEvents}
                            getAllEvents={getAllEvents}
                        />
                    </Grid>
                    <Grid item md={3.5}>
                        <Activity
                            getEvents={getEvents}
                            getAllEvents={getAllEvents}
                        />
                        <Notification />
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    )
}

export default Calendar;